import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAccess } from "@/context/AccessContext";
import { GIGA_IMAGES } from "@/lib/images";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AccessGate — cinematic classified-terminal entrance.
 * Locks the entire site behind a password gate. On success, fades away.
 */
export default function AccessGate({ onRequestBriefing }) {
  const { unlocked, loading, grant } = useAccess();
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle | verifying | error | granted
  const [error, setError] = useState("");
  const [fadingOut, setFadingOut] = useState(false);
  const [mounted, setMounted] = useState(false);
  const inputRef = useRef(null);
  const shakeRef = useRef(null);

  useEffect(() => {
    if (!loading && !unlocked) {
      // slight delay so the curtain reveal feels intentional
      const t = setTimeout(() => setMounted(true), 40);
      setTimeout(() => inputRef.current?.focus(), 260);
      return () => clearTimeout(t);
    }
  }, [loading, unlocked]);

  // Prevent body scroll while locked
  useEffect(() => {
    if (loading || unlocked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [loading, unlocked]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (status === "verifying" || !password.trim()) return;
    setStatus("verifying");
    setError("");
    try {
      // Force a visible scan moment even on very fast connections
      const [res] = await Promise.all([
        axios.post(`${API}/access`, { password }),
        new Promise((r) => setTimeout(r, 900)),
      ]);
      setStatus("granted");
      // Brief "GRANTED" moment, then fade the whole gate
      setTimeout(() => {
        setFadingOut(true);
        setTimeout(() => {
          grant(res.data);
        }, 700);
      }, 650);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string" && err?.response?.status === 401
          ? "Access denied. Code not recognised."
          : "Verification failed. Please try again."
      );
      setStatus("error");
      // Trigger shake
      if (shakeRef.current) {
        shakeRef.current.classList.remove("g1-shake");
        // force reflow
        // eslint-disable-next-line no-unused-expressions
        shakeRef.current.offsetWidth;
        shakeRef.current.classList.add("g1-shake");
      }
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  };

  if (loading || unlocked) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Access restricted"
      data-testid="access-gate"
      className={`fixed inset-0 z-[200] bg-[color:var(--g1-bone)] text-[color:var(--g1-ink)] transition-opacity duration-700 ease-out ${
        fadingOut ? "opacity-0 pointer-events-none" : "opacity-100"
      } ${mounted ? "" : "opacity-0"}`}
      style={{ transitionDuration: fadingOut ? "700ms" : "400ms" }}
    >
      {/* Backdrop — abstract orchestration plate at low opacity */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <img
          src={GIGA_IMAGES.systemsField}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: "grayscale(1) contrast(1.2) brightness(1.04)",
            opacity: 0.045,
            mixBlendMode: "multiply",
          }}
        />
        {/* Stronger edge vignette so corner labels read crisply */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(244,243,240,0) 35%, rgba(244,243,240,0.85) 100%)",
          }}
        />
      </div>

      {/* Frame hairlines */}
      <div
        aria-hidden
        className="absolute inset-5 md:inset-8 pointer-events-none border border-[color:var(--g1-rule)]"
      />
      <div
        aria-hidden
        className="absolute inset-5 md:inset-8 pointer-events-none g1-corners"
      >
        <span className="tl" />
        <span className="tr" />
        <span className="bl" />
        <span className="br" />
      </div>

      {/* Scanning line during verification */}
      {status === "verifying" && <ScanningLine />}

      {/* Center composition */}
      <div className="relative h-full w-full flex items-center justify-center px-6 md:px-14">
        <div
          ref={shakeRef}
          className="w-full max-w-[920px]"
          data-testid="access-gate-panel"
        >
          {/* Eyebrow */}
          <div className="g1-label mb-6 text-[color:var(--g1-ink-2)]">
            / ACCESS REQUIRED
          </div>

          {/* Headline */}
          <h1
            className="g1-display text-[56px] sm:text-[80px] md:text-[112px] lg:text-[136px] leading-[0.92]"
            data-testid="access-gate-heading"
          >
            Restricted.
          </h1>

          {/* Lede */}
          <p className="g1-lede mt-6 md:mt-8 max-w-[42ch] text-[color:var(--g1-ink-2)]">
            This site is held for reviewed audiences.{" "}
            <span className="g1-display-italic">
              Enter your access code to continue.
            </span>
          </p>

          {/* Form */}
          <form onSubmit={onSubmit} noValidate className="mt-10 md:mt-14 max-w-[640px]">
            <div className="grid grid-cols-12 gap-5 md:gap-6 items-end">
              <div className="col-span-12 sm:col-span-8">
                <label
                  htmlFor="g1-access-code"
                  className="g1-label block mb-2"
                >
                  Access Code
                </label>
                <input
                  ref={inputRef}
                  id="g1-access-code"
                  name="access"
                  type="password"
                  autoComplete="off"
                  spellCheck="false"
                  className="g1-input font-mono tracking-[0.16em] text-lg"
                  placeholder="— — — — — —"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (status === "error") {
                      setStatus("idle");
                      setError("");
                    }
                  }}
                  disabled={status === "verifying" || status === "granted"}
                  data-testid="access-input"
                />
              </div>
              <div className="col-span-12 sm:col-span-4">
                <button
                  type="submit"
                  className="g1-btn g1-btn-primary w-full"
                  disabled={status === "verifying" || status === "granted"}
                  data-testid="access-submit"
                >
                  {status === "verifying"
                    ? "Verifying…"
                    : status === "granted"
                    ? "Granted →"
                    : "Authenticate"}
                  {status !== "verifying" && status !== "granted" && (
                    <span className="g1-btn-arrow" aria-hidden>
                      →
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Message slot */}
            <div className="mt-5 min-h-[22px]" aria-live="polite">
              {status === "error" && (
                <p
                  data-testid="access-error"
                  className="font-mono text-[11px] tracking-[0.2em] uppercase text-[color:var(--g1-olive)]"
                >
                  × {error}
                </p>
              )}
              {status === "granted" && (
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[color:var(--g1-ink)]">
                  ✓ Access granted — opening terminal
                </p>
              )}
              {status === "idle" && (
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[color:var(--g1-ink-3)]">
                  All entries are logged. Session expires in 7 days.
                </p>
              )}
              {status === "verifying" && (
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[color:var(--g1-ink-2)]">
                  · · · Verifying credential against vault
                </p>
              )}
            </div>
          </form>

          {/* Secondary path */}
          <div className="mt-14 md:mt-20 pt-6 border-t border-[color:var(--g1-rule)] flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <p className="g1-label text-[color:var(--g1-ink-2)] max-w-[60ch]">
              No access code? Briefings are granted on a reviewed basis.
            </p>
            <button
              type="button"
              onClick={onRequestBriefing}
              className="g1-link font-mono text-[12px] tracking-[0.2em] uppercase"
              data-testid="access-request-briefing"
            >
              Request a Briefing →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusLine({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 font-mono font-medium text-[11px] tracking-[0.22em] uppercase">
      <span className="text-[color:var(--g1-ink-2)]">{label}</span>
      <span className="min-w-[104px] text-right text-[color:var(--g1-ink)]">
        {value}
      </span>
    </div>
  );
}

function sessionValue(s) {
  return s === "granted" ? "ACTIVE" : s === "verifying" ? "NEGOTIATING" : "PENDING";
}
function vaultValue(s) {
  return s === "granted" ? "OPEN" : "SEALED";
}
function clearanceValue(s) {
  if (s === "granted") return "AUTHORISED";
  if (s === "error") return "DENIED";
  return "REQUIRED";
}

function ScanningLine() {
  return (
    <div aria-hidden className="absolute inset-5 md:inset-8 overflow-hidden pointer-events-none">
      <div className="g1-scanline" />
    </div>
  );
}
